/**
 * Centralized Icon System for Zubduq CRM
 * Provides consistent SVG icons across the platform
 * Based on the professional design from index.html
 *
 * @author Zubduq Development Team
 * @version 1.0.0
 */

export const ICON_COLORS = {
  primary: '#3B82F6',    // Blue - Appointments/Primary actions
  success: '#10B981',    // Green - Leads/Success states
  warning: '#F59E0B',    // Yellow/Orange - Organizations/Warnings
  secondary: '#6B7280',  // Gray - Secondary actions
  purple: '#8B5CF6',     // Purple - Connections/Advanced features
  dark: '#1E40AF'        // Dark blue - Headers/Important elements
};

export const ICONS = {
  // Branding Icons
  logo: {
    svg: `<svg width="24" height="24" viewBox="0 0 180 180" fill="none">
      <defs>
        <linearGradient id="brand-grad-mark-icon" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stop-color="#1e3a8a" />
          <stop offset="50%" stop-color="#3b82f6" />
          <stop offset="100%" stop-color="#06b6d4" />
        </linearGradient>
      </defs>
      <rect width="180" height="180" rx="44" fill="url(#brand-grad-mark-icon)"/>
      <text x="90" y="85" font-family="'Segoe UI','Segoe UI Web (West European)',system-ui" font-size="54" font-weight="700" text-anchor="middle" fill="#FFFFFF" letter-spacing="1">ZUB</text>
      <text x="90" y="132" font-family="'Segoe UI','Segoe UI Web (West European)',system-ui" font-size="54" font-weight="700" text-anchor="middle" fill="#FFFFFF" letter-spacing="1">DUQ</text>
    </svg>`,
    color: ICON_COLORS.primary,
    emoji: '🎯'
  },

  favicon: {
    svg: `<svg width="24" height="24" viewBox="0 0 180 180" fill="none">
      <defs>
        <linearGradient id="brand-grad-favicon" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stop-color="#1e3a8a" />
          <stop offset="50%" stop-color="#3b82f6" />
          <stop offset="100%" stop-color="#06b6d4" />
        </linearGradient>
      </defs>
      <rect width="180" height="180" rx="44" fill="url(#brand-grad-favicon)"/>
      <text x="90" y="85" font-family="'Segoe UI','Segoe UI Web (West European)',system-ui" font-size="54" font-weight="700" text-anchor="middle" fill="#FFFFFF" letter-spacing="1">ZUB</text>
      <text x="90" y="132" font-family="'Segoe UI','Segoe UI Web (West European)',system-ui" font-size="54" font-weight="700" text-anchor="middle" fill="#FFFFFF" letter-spacing="1">DUQ</text>
    </svg>`,
    color: ICON_COLORS.primary,
    emoji: '🎯'
  },

  brand: {
    svg: `<svg width="24" height="24" viewBox="0 0 180 180" fill="none">
      <defs>
        <linearGradient id="brand-grad-brand" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stop-color="#1e3a8a" />
          <stop offset="50%" stop-color="#3b82f6" />
          <stop offset="100%" stop-color="#06b6d4" />
        </linearGradient>
      </defs>
      <rect width="180" height="180" rx="44" fill="url(#brand-grad-brand)"/>
      <text x="90" y="85" font-family="'Segoe UI','Segoe UI Web (West European)',system-ui" font-size="54" font-weight="700" text-anchor="middle" fill="#FFFFFF" letter-spacing="1">ZUB</text>
      <text x="90" y="132" font-family="'Segoe UI','Segoe UI Web (West European)',system-ui" font-size="54" font-weight="700" text-anchor="middle" fill="#FFFFFF" letter-spacing="1">DUQ</text>
    </svg>`,
    color: ICON_COLORS.primary,
    emoji: '🎯'
  },

  // Core Navigation Icons
  home: {
    svg: `<svg width="24" height="24" viewBox="0 0 24 24" fill="none">
      <!-- House base -->
      <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" fill="#3B82F6"/>
      <!-- Door -->
      <rect x="9" y="12" width="6" height="10" rx="1" fill="#1E40AF"/>
      <!-- Window -->
      <circle cx="15" cy="15" r="1.5" fill="#FFFFFF"/>
    </svg>`,
    color: ICON_COLORS.primary,
    emoji: '🏠' // Fallback for backwards compatibility
  },

  // Appointment Management Icons (from index.html)
  appointments: {
    svg: `<svg width="24" height="24" viewBox="0 0 24 24" fill="none">
      <!-- Simple calendar -->
      <rect x="5" y="6" width="14" height="13" rx="3" fill="#3B82F6"/>
      <!-- Calendar header -->
      <rect x="5" y="6" width="14" height="4" rx="3" fill="#1E40AF"/>
      <!-- Calendar rings -->
      <line x1="9" y1="4" x2="9" y2="8" stroke="#6B7280" stroke-width="1.5" stroke-linecap="round"/>
      <line x1="15" y1="4" x2="15" y2="8" stroke="#6B7280" stroke-width="1.5" stroke-linecap="round"/>
      <!-- Simple date dot -->
      <circle cx="12" cy="14" r="1.5" fill="#FFFFFF"/>
    </svg>`,
    color: ICON_COLORS.primary,
    emoji: '📅'
  },

  schedule: {
    svg: `<svg width="24" height="24" viewBox="0 0 24 24" fill="none">
      <!-- Simple calendar -->
      <rect x="5" y="6" width="14" height="13" rx="3" fill="#3B82F6"/>
      <!-- Calendar header -->
      <rect x="5" y="6" width="14" height="4" rx="3" fill="#1E40AF"/>
      <!-- Calendar rings -->
      <line x1="9" y1="4" x2="9" y2="8" stroke="#6B7280" stroke-width="1.5" stroke-linecap="round"/>
      <line x1="15" y1="4" x2="15" y2="8" stroke="#6B7280" stroke-width="1.5" stroke-linecap="round"/>
      <!-- Simple date dot -->
      <circle cx="12" cy="14" r="1.5" fill="#FFFFFF"/>
    </svg>`,
    color: ICON_COLORS.primary,
    emoji: '🗓️'
  },

  queue: {
    svg: `<svg width="24" height="24" viewBox="0 0 24 24" fill="none">
      <!-- Queue list with numbers -->
      <rect x="4" y="5" width="16" height="4" rx="2" fill="#3B82F6"/>
      <rect x="4" y="10" width="16" height="4" rx="2" fill="#3B82F6" opacity="0.7"/>
      <rect x="4" y="15" width="16" height="4" rx="2" fill="#3B82F6" opacity="0.5"/>
      <!-- Numbered dots -->
      <circle cx="7" cy="7" r="1.2" fill="#FFFFFF"/>
      <circle cx="7" cy="12" r="1.2" fill="#FFFFFF"/>
      <circle cx="7" cy="17" r="1.2" fill="#FFFFFF"/>
    </svg>`,
    color: ICON_COLORS.primary,
    emoji: '📋'
  },

  // Lead Management Icons (from index.html)
  leads: {
    svg: `<svg width="24" height="24" viewBox="0 0 24 24" fill="none">
      <!-- Person -->
      <circle cx="12" cy="8" r="4" fill="#10B981"/>
      <!-- Follow-up indicator -->
      <circle cx="16" cy="16" r="6" fill="#F59E0B"/>
      <path d="M14 16 L15 17 L18 14" stroke="#FFFFFF" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
    </svg>`,
    color: ICON_COLORS.success,
    emoji: '👥'
  },

  customers: {
    svg: `<svg width="24" height="24" viewBox="0 0 24 24" fill="none">
      <!-- Person -->
      <circle cx="12" cy="8" r="4" fill="#10B981"/>
      <!-- Follow-up indicator -->
      <circle cx="16" cy="16" r="6" fill="#F59E0B"/>
      <path d="M14 16 L15 17 L18 14" stroke="#FFFFFF" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
    </svg>`,
    color: ICON_COLORS.success,
    emoji: '👥'
  },

  // Organization Icons (from index.html)
  organizations: {
    svg: `<svg width="24" height="24" viewBox="0 0 24 24" fill="none">
      <!-- Three simple buildings -->
      <rect x="4" y="10" width="4" height="10" rx="2" fill="#3B82F6"/>
      <rect x="10" y="8" width="4" height="12" rx="2" fill="#10B981"/>
      <rect x="16" y="12" width="4" height="8" rx="2" fill="#F59E0B"/>
      <!-- Simple connection line -->
      <line x1="6" y1="6" x2="18" y2="6" stroke="#8B5CF6" stroke-width="1.5" stroke-linecap="round"/>
    </svg>`,
    color: ICON_COLORS.warning,
    emoji: '🏢'
  },

  // Dashboard & Analytics Icons
  dashboard: {
    svg: `<svg width="24" height="24" viewBox="0 0 24 24" fill="none">
      <!-- Dashboard tiles with different colors -->
      <rect x="3" y="3" width="7" height="7" rx="2" fill="#3B82F6"/>
      <rect x="14" y="3" width="7" height="5" rx="2" fill="#10B981"/>
      <rect x="14" y="12" width="7" height="9" rx="2" fill="#F59E0B"/>
      <rect x="3" y="14" width="7" height="7" rx="2" fill="#8B5CF6"/>
      <!-- Small indicators -->
      <circle cx="6.5" cy="6.5" r="1" fill="#FFFFFF"/>
      <circle cx="17.5" cy="5.5" r="1" fill="#FFFFFF"/>
      <circle cx="17.5" cy="16.5" r="1" fill="#FFFFFF"/>
      <circle cx="6.5" cy="17.5" r="1" fill="#FFFFFF"/>
    </svg>`,
    color: ICON_COLORS.primary,
    emoji: '📊'
  },

  analytics: {
    svg: `<svg width="24" height="24" viewBox="0 0 24 24" fill="none">
      <!-- Chart bars with gradient colors -->
      <rect x="4" y="16" width="3" height="5" rx="1" fill="#3B82F6"/>
      <rect x="8" y="12" width="3" height="9" rx="1" fill="#10B981"/>
      <rect x="12" y="8" width="3" height="13" rx="1" fill="#F59E0B"/>
      <rect x="16" y="6" width="3" height="15" rx="1" fill="#8B5CF6"/>
      <!-- Trend line -->
      <path d="M5.5 18 L9.5 14 L13.5 10 L17.5 8" stroke="#EF4444" stroke-width="2" stroke-linecap="round"/>
      <!-- Data points -->
      <circle cx="5.5" cy="18" r="1.5" fill="#EF4444"/>
      <circle cx="9.5" cy="14" r="1.5" fill="#EF4444"/>
      <circle cx="13.5" cy="10" r="1.5" fill="#EF4444"/>
      <circle cx="17.5" cy="8" r="1.5" fill="#EF4444"/>
    </svg>`,
    color: ICON_COLORS.primary,
    emoji: '📊'
  },

  // Admin & Settings Icons
  admin: {
    svg: `<svg width="24" height="24" viewBox="0 0 24 24" fill="none">
      <!-- Admin shield/gear hybrid -->
      <circle cx="12" cy="12" r="8" fill="#8B5CF6"/>
      <circle cx="12" cy="12" r="4" fill="#3B82F6"/>
      <!-- Gear teeth -->
      <rect x="11" y="2" width="2" height="4" rx="1" fill="#F59E0B"/>
      <rect x="11" y="18" width="2" height="4" rx="1" fill="#F59E0B"/>
      <rect x="2" y="11" width="4" height="2" rx="1" fill="#F59E0B"/>
      <rect x="18" y="11" width="4" height="2" rx="1" fill="#F59E0B"/>
      <!-- Center indicator -->
      <circle cx="12" cy="12" r="2" fill="#FFFFFF"/>
    </svg>`,
    color: ICON_COLORS.secondary,
    emoji: '⚙️'
  },

  // Service Management Icons
  services: {
    svg: `<svg width="24" height="24" viewBox="0 0 24 24" fill="none">
      <!-- Service box icon with green background -->
      <circle cx="12" cy="12" r="10" fill="#10B981"/>
      <path d="M7 7h10v10H7z" fill="none" stroke="#FFFFFF" stroke-width="2"/>
      <path d="M9 9h6v6H9z" fill="#FFFFFF"/>
    </svg>`,
    color: ICON_COLORS.success,
    emoji: '🛍️'
  },

  service: {
    svg: `<svg width="24" height="24" viewBox="0 0 24 24" fill="none">
      <!-- Service box icon with green background -->
      <circle cx="12" cy="12" r="10" fill="#10B981"/>
      <path d="M7 7h10v10H7z" fill="none" stroke="#FFFFFF" stroke-width="2"/>
      <path d="M9 9h6v6H9z" fill="#FFFFFF"/>
    </svg>`,
    color: ICON_COLORS.success,
    emoji: '🛍️'
  },

  // Product Management Icon
  products: {
    svg: `<svg width="24" height="24" viewBox="0 0 24 24" fill="none">
      <!-- Product box/package icon -->
      <circle cx="12" cy="12" r="10" fill="#8B5CF6"/>
      <!-- Box outline -->
      <path d="M8 10L12 8L16 10V16L12 18L8 16V10Z" fill="#FFFFFF"/>
      <!-- Box center line -->
      <path d="M12 8V18M8 10L12 12L16 10" stroke="#8B5CF6" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
    </svg>`,
    color: ICON_COLORS.purple,
    emoji: '📦'
  },

  package: {
    svg: `<svg width="24" height="24" viewBox="0 0 24 24" fill="none">
      <!-- Product box/package icon -->
      <circle cx="12" cy="12" r="10" fill="#8B5CF6"/>
      <!-- Box outline -->
      <path d="M8 10L12 8L16 10V16L12 18L8 16V10Z" fill="#FFFFFF"/>
      <!-- Box center line -->
      <path d="M12 8V18M8 10L12 12L16 10" stroke="#8B5CF6" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
    </svg>`,
    color: ICON_COLORS.purple,
    emoji: '📦'
  },

  // Action Icons
  add: {
    svg: `<svg width="24" height="24" viewBox="0 0 24 24" fill="none">
      <!-- Plus icon with circular background -->
      <circle cx="12" cy="12" r="10" fill="#10B981"/>
      <line x1="12" y1="8" x2="12" y2="16" stroke="#FFFFFF" stroke-width="3" stroke-linecap="round"/>
      <line x1="8" y1="12" x2="16" y2="12" stroke="#FFFFFF" stroke-width="3" stroke-linecap="round"/>
    </svg>`,
    color: ICON_COLORS.success,
    emoji: '➕'
  },

  check: {
    svg: `<svg width="24" height="24" viewBox="0 0 24 24" fill="none">
      <!-- Check mark with circular background -->
      <circle cx="12" cy="12" r="10" fill="#10B981"/>
      <path d="M9 12l2 2 4-4" stroke="#FFFFFF" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/>
    </svg>`,
    color: ICON_COLORS.success,
    emoji: '✅'
  },

  close: {
    svg: `<svg width="24" height="24" viewBox="0 0 24 24" fill="none">
      <!-- X icon with circular background -->
      <circle cx="12" cy="12" r="10" fill="#EF4444"/>
      <line x1="15" y1="9" x2="9" y2="15" stroke="#FFFFFF" stroke-width="3" stroke-linecap="round"/>
      <line x1="9" y1="9" x2="15" y2="15" stroke="#FFFFFF" stroke-width="3" stroke-linecap="round"/>
    </svg>`,
    color: '#EF4444',
    emoji: '❌'
  },

  minus: {
    svg: `<svg width="24" height="24" viewBox="0 0 24 24" fill="none">
      <!-- Minus icon with circular background -->
      <circle cx="12" cy="12" r="10" fill="#6B7280"/>
      <line x1="8" y1="12" x2="16" y2="12" stroke="#FFFFFF" stroke-width="3" stroke-linecap="round"/>
    </svg>`,
    color: ICON_COLORS.secondary,
    emoji: '➖'
  },

  cancel: {
    svg: `<svg width="24" height="24" viewBox="0 0 24 24" fill="none">
      <!-- X icon with circular background -->
      <circle cx="12" cy="12" r="10" fill="#EF4444"/>
      <line x1="15" y1="9" x2="9" y2="15" stroke="#FFFFFF" stroke-width="3" stroke-linecap="round"/>
      <line x1="9" y1="9" x2="15" y2="15" stroke="#FFFFFF" stroke-width="3" stroke-linecap="round"/>
    </svg>`,
    color: ICON_COLORS.warning,
    emoji: '❌'
  },

  delete: {
    svg: `<svg width="24" height="24" viewBox="0 0 24 24" fill="none">
      <!-- Colorful trash/delete icon -->
      <path d="M3 6h18v2H3z" fill="#EF4444"/>
      <path d="M19 8v11a2 2 0 01-2 2H7a2 2 0 01-2-2V8h14z" fill="#EF4444"/>
      <path d="M10 11v6" stroke="#FFFFFF" stroke-width="2" stroke-linecap="round"/>
      <path d="M14 11v6" stroke="#FFFFFF" stroke-width="2" stroke-linecap="round"/>
      <path d="M9 5V3a1 1 0 011-1h4a1 1 0 011 1v2" stroke="#6B7280" stroke-width="2" stroke-linecap="round"/>
    </svg>`,
    color: '#EF4444',
    emoji: '🗑️'
  },

  category: {
    svg: `<svg width="24" height="24" viewBox="0 0 24 24" fill="none">
      <!-- Tags/categories icon -->
      <path d="M20.59 13.41l-7.17 7.17a2 2 0 01-2.83 0L2 12V2h10l8.59 8.59a2 2 0 010 2.82z" fill="#F59E0B"/>
      <circle cx="9" cy="9" r="2" fill="#FFFFFF"/>
    </svg>`,
    color: ICON_COLORS.warning,
    emoji: '🏷️'
  },

  copy: {
    svg: `<svg width="16" height="16" viewBox="0 0 24 24" fill="none">
      <!-- Elegant colorful clipboard icon -->
      <defs>
        <!-- Gradient for clipboard background -->
        <linearGradient id="clipboardGrad" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" style="stop-color:#f8fafc;stop-opacity:1" />
          <stop offset="100%" style="stop-color:#e2e8f0;stop-opacity:1" />
        </linearGradient>
        <!-- Gradient for clip -->
        <linearGradient id="clipGrad" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" style="stop-color:#3b82f6;stop-opacity:1" />
          <stop offset="100%" style="stop-color:#1d4ed8;stop-opacity:1" />
        </linearGradient>
        <!-- Shadow filter -->
        <filter id="clipboardShadow" x="-20%" y="-20%" width="140%" height="140%">
          <feDropShadow dx="0" dy="1" stdDeviation="1" flood-color="#64748b" flood-opacity="0.15"/>
        </filter>
      </defs>
      <!-- Clipboard background with elegant shadow -->
      <rect x="4" y="2" width="13" height="18" rx="2.5"
            fill="url(#clipboardGrad)"
            stroke="#cbd5e1"
            stroke-width="1.5"
            filter="url(#clipboardShadow)"/>
      <!-- Sleek clipboard clip with gradient -->
      <rect x="8" y="0.5" width="5" height="3.5" rx="1.5"
            fill="url(#clipGrad)"
            stroke="#1e40af"
            stroke-width="0.5"/>
      <!-- Elegant document content lines -->
      <path d="M7 8h7M7 11h7M7 14h5"
            stroke="#475569"
            stroke-width="1.2"
            stroke-linecap="round"
            opacity="0.7"/>
      <!-- Subtle highlight on clip -->
      <rect x="8.5" y="1" width="4" height="1" rx="0.5"
            fill="#60a5fa"
            opacity="0.6"/>
    </svg>`,
    color: ICON_COLORS.secondary,
    emoji: '📋'
  },

  download: {
    svg: `<svg width="24" height="24" viewBox="0 0 24 24" fill="none">
      <!-- Download icon with colorful background -->
      <circle cx="12" cy="12" r="10" fill="#3B82F6"/>
      <!-- Arrow pointing down -->
      <path d="M12 7v6m0 0l3-3m-3 3l-3-3" stroke="#FFFFFF" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
      <!-- Bottom line -->
      <path d="M8 17h8" stroke="#FFFFFF" stroke-width="2" stroke-linecap="round"/>
    </svg>`,
    color: ICON_COLORS.primary,
    emoji: '⬇️'
  },

  upload: {
    svg: `<svg width="24" height="24" viewBox="0 0 24 24" fill="none">
      <!-- Upload icon with colorful background -->
      <circle cx="12" cy="12" r="10" fill="#10B981"/>
      <!-- Arrow pointing up -->
      <path d="M12 17V7m0 0L9 10m3-3l3 3" stroke="#FFFFFF" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
      <!-- Bottom line -->
      <path d="M8 17h8" stroke="#FFFFFF" stroke-width="2" stroke-linecap="round"/>
    </svg>`,
    color: ICON_COLORS.success,
    emoji: '⬆️'
  },

  'chevron-up': {
    svg: `<svg width="24" height="24" viewBox="0 0 24 24" fill="none">
      <!-- Simple chevron up -->
      <path d="M18 15L12 9L6 15" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
    </svg>`,
    color: ICON_COLORS.secondary,
    emoji: '⬆️'
  },

  'chevron-down': {
    svg: `<svg width="24" height="24" viewBox="0 0 24 24" fill="none">
      <!-- Simple chevron down -->
      <path d="M6 9L12 15L18 9" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
    </svg>`,
    color: ICON_COLORS.secondary,
    emoji: '⬇️'
  },

  clipboard: {
    svg: `<svg width="24" height="24" viewBox="0 0 24 24" fill="none">
      <!-- Clipboard with colorful background -->
      <rect x="6" y="4" width="12" height="16" rx="2" fill="#6B7280"/>
      <!-- Clipboard header -->
      <rect x="8" y="2" width="8" height="4" rx="1" fill="#3B82F6"/>
      <!-- Content lines -->
      <line x1="9" y1="9" x2="15" y2="9" stroke="#FFFFFF" stroke-width="1.5" stroke-linecap="round"/>
      <line x1="9" y1="12" x2="15" y2="12" stroke="#FFFFFF" stroke-width="1.5" stroke-linecap="round"/>
      <line x1="9" y1="15" x2="13" y2="15" stroke="#FFFFFF" stroke-width="1.5" stroke-linecap="round"/>
    </svg>`,
    color: ICON_COLORS.secondary,
    emoji: '📋'
  },

  refresh: {
    svg: `<svg width="24" height="24" viewBox="0 0 24 24" fill="none">
      <!-- Refresh/sync circular arrows -->
      <path d="M21 12a9 9 0 11-6.219-8.56" stroke="#3B82F6" stroke-width="2" stroke-linecap="round"/>
      <path d="M15 4.5l-.5-3 3 .5" stroke="#3B82F6" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
      <path d="M3 12a9 9 0 006.219 8.56" stroke="#10B981" stroke-width="2" stroke-linecap="round"/>
      <path d="M9 19.5l.5 3-3-.5" stroke="#10B981" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
    </svg>`,
    color: ICON_COLORS.success,
    emoji: '🔄'
  },

  link: {
    svg: `<svg width="24" height="24" viewBox="0 0 24 24" fill="none">
      <!-- Colorful link icon -->
      <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" fill="#8B5CF6"/>
      <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" fill="#F59E0B"/>
      <!-- Link connection -->
      <circle cx="12" cy="12" r="2" fill="#3B82F6"/>
    </svg>`,
    color: ICON_COLORS.secondary,
    emoji: '🔗'
  },

  // User & Employee Icons
  user: {
    svg: `<svg width="24" height="24" viewBox="0 0 24 24" fill="none">
      <!-- Modern flat user icon inspired by leads icon -->
      <!-- Head - teal/cyan circle -->
      <circle cx="12" cy="7" r="3.5" fill="#06B6D4"/>
      <!-- Body/shoulders - purple rounded shape with proper spacing -->
      <path d="M6 18c0-3.3 2.7-6 6-6s6 2.7 6 6v4c0 1.1-0.9 2-2 2H8c-1.1 0-2-0.9-2-2v-4z" fill="#8B5CF6"/>
    </svg>`,
    color: ICON_COLORS.secondary,
    emoji: '👤'
  },

  team: {
    svg: `<svg width="24" height="24" viewBox="0 0 24 24" fill="none">
      <!-- Team of people with different colors -->
      <circle cx="9" cy="7" r="3" fill="#3B82F6"/>
      <circle cx="15" cy="8" r="2.5" fill="#10B981"/>
      <path d="M3 19v-2a4 4 0 014-4h4a4 4 0 014 4v2" fill="#8B5CF6"/>
      <path d="M16 19v-1.5a3 3 0 013-3h1a3 3 0 013 3V19" fill="#F59E0B"/>
      <!-- Face details -->
      <circle cx="8" cy="6.5" r="0.5" fill="#FFFFFF"/>
      <circle cx="10" cy="6.5" r="0.5" fill="#FFFFFF"/>
    </svg>`,
    color: ICON_COLORS.secondary,
    emoji: '👥'
  },

  // Time & Availability Icons
  time: {
    svg: `<svg width="24" height="24" viewBox="0 0 24 24" fill="none">
      <!-- Colorful clock -->
      <circle cx="12" cy="12" r="10" fill="#3B82F6"/>
      <circle cx="12" cy="12" r="8" fill="#FFFFFF"/>
      <!-- Clock hands -->
      <line x1="12" y1="6" x2="12" y2="12" stroke="#1E40AF" stroke-width="3" stroke-linecap="round"/>
      <line x1="12" y1="12" x2="16" y2="14" stroke="#F59E0B" stroke-width="2" stroke-linecap="round"/>
      <!-- Hour markers -->
      <circle cx="12" cy="5" r="1" fill="#10B981"/>
      <circle cx="19" cy="12" r="1" fill="#10B981"/>
      <circle cx="12" cy="19" r="1" fill="#10B981"/>
      <circle cx="5" cy="12" r="1" fill="#10B981"/>
      <!-- Center -->
      <circle cx="12" cy="12" r="1.5" fill="#EF4444"/>
    </svg>`,
    color: ICON_COLORS.secondary,
    emoji: '⏰'
  },

  // Support & Contact Icons
  support: {
    svg: `<svg width="24" height="24" viewBox="0 0 24 24" fill="none">
      <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2"/>
      <path d="M8 14s1.5 2 4 2 4-2 4-2" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
      <line x1="9" y1="9" x2="9.01" y2="9" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
      <line x1="15" y1="9" x2="15.01" y2="9" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
    </svg>`,
    color: ICON_COLORS.secondary,
    emoji: '💬'
  },

  contact: {
    svg: `<svg width="24" height="24" viewBox="0 0 24 24" fill="none">
      <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
    </svg>`,
    color: ICON_COLORS.secondary,
    emoji: '📞'
  },

  followup: {
    svg: `<svg width="24" height="24" viewBox="0 0 24 24" fill="none">
      <!-- Colorful follow-up icon: person + arrow + checkmark -->
      <!-- Person circle -->
      <circle cx="8" cy="8" r="4" fill="#10B981"/>
      <!-- Follow-up arrow -->
      <path d="M12 8h8m0 0l-3-3m3 3l-3 3" stroke="#3B82F6" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
      <!-- Success indicator -->
      <circle cx="18" cy="16" r="4" fill="#F59E0B"/>
      <path d="M16 16l1.5 1.5L20 15" stroke="#FFFFFF" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
      <!-- Connection line -->
      <path d="M8 12v4a2 2 0 002 2h4" stroke="#8B5CF6" stroke-width="2" stroke-linecap="round"/>
    </svg>`,
    color: ICON_COLORS.success,
    emoji: '📋'
  },

  // Authentication Icons
  login: {
    svg: `<svg width="24" height="24" viewBox="0 0 24 24" fill="none">
      <!-- Login door with arrow -->
      <rect x="3" y="4" width="14" height="16" rx="2" fill="#10B981"/>
      <circle cx="10" cy="10" r="1" fill="#FFFFFF"/>
      <!-- Arrow pointing in -->
      <path d="M18 12h-6m0 0l2-2m-2 2l2 2" stroke="#3B82F6" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/>
    </svg>`,
    color: ICON_COLORS.success,
    emoji: '🔐'
  },

  logout: {
    svg: `<svg width="24" height="24" viewBox="0 0 24 24" fill="none">
      <!-- Clear logout: person figure + exit arrow -->
      <!-- Person -->
      <circle cx="9" cy="7" r="3" fill="#3B82F6"/>
      <path d="M5 21v-2a4 4 0 014-4h0a4 4 0 014 4v2" stroke="#3B82F6" stroke-width="2" stroke-linecap="round"/>
      <!-- Exit arrow pointing right -->
      <path d="M14 12h7m0 0l-3-3m3 3l-3 3" stroke="#EF4444" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
      <!-- Vertical line (door frame) -->
      <line x1="14" y1="4" x2="14" y2="20" stroke="#6B7280" stroke-width="2"/>
    </svg>`,
    color: ICON_COLORS.warning,
    emoji: '🚪'
  },

  // Action Icons
  edit: {
    svg: `<svg width="24" height="24" viewBox="0 0 24 24" fill="none">
      <!-- Colorful edit pencil -->
      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" stroke="#6B7280" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
      <path d="m18.5 2.5 3 3L14 13l-4 1 1-4 7.5-7.5z" fill="#3B82F6" stroke="#2563EB" stroke-width="1"/>
      <circle cx="19.5" cy="4.5" r="1.5" fill="#F59E0B"/>
    </svg>`,
    color: ICON_COLORS.primary,
    emoji: '✏️'
  },

  // Navigation Icons
  dropdown: {
    svg: `<svg width="24" height="24" viewBox="0 0 24 24" fill="none">
      <!-- Colorful dropdown arrow -->
      <path d="M6 9l6 6 6-6" stroke="#6B7280" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/>
      <circle cx="12" cy="12" r="1" fill="#3B82F6"/>
    </svg>`,
    color: ICON_COLORS.secondary,
    emoji: '⬇️'
  },

  // Additional icons for FontAwesome replacement
  warning: {
    svg: `<svg width="24" height="24" viewBox="0 0 24 24" fill="none">
      <circle cx="12" cy="12" r="10" fill="#F59E0B"/>
      <path d="M12 8v4" stroke="#FFFFFF" stroke-width="2" stroke-linecap="round"/>
      <circle cx="12" cy="16" r="1" fill="#FFFFFF"/>
    </svg>`,
    color: '#F59E0B',
    emoji: '⚠️'
  },

  info: {
    svg: `<svg width="24" height="24" viewBox="0 0 24 24" fill="none">
      <circle cx="12" cy="12" r="10" fill="#3B82F6"/>
      <path d="M12 16v-4" stroke="#FFFFFF" stroke-width="2" stroke-linecap="round"/>
      <circle cx="12" cy="8" r="1" fill="#FFFFFF"/>
    </svg>`,
    color: '#3B82F6',
    emoji: 'ℹ️'
  },

  view: {
    svg: `<svg width="24" height="24" viewBox="0 0 24 24" fill="none">
      <circle cx="12" cy="12" r="3" fill="#3B82F6"/>
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" stroke="#6B7280" stroke-width="2"/>
    </svg>`,
    color: '#3B82F6',
    emoji: '👁️'
  },

  notes: {
    svg: `<svg width="24" height="24" viewBox="0 0 24 24" fill="none">
      <rect x="3" y="3" width="18" height="18" rx="2" fill="#F59E0B"/>
      <path d="M9 7h6M9 11h6M9 15h4" stroke="#FFFFFF" stroke-width="2" stroke-linecap="round"/>
    </svg>`,
    color: '#F59E0B',
    emoji: '📝'
  },

  pause: {
    svg: `<svg width="24" height="24" viewBox="0 0 24 24" fill="none">
      <circle cx="12" cy="12" r="10" fill="#6B7280"/>
      <rect x="9" y="8" width="2" height="8" fill="#FFFFFF"/>
      <rect x="13" y="8" width="2" height="8" fill="#FFFFFF"/>
    </svg>`,
    color: '#6B7280',
    emoji: '⏸️'
  },

  archive: {
    svg: `<svg width="24" height="24" viewBox="0 0 24 24" fill="none">
      <rect x="3" y="6" width="18" height="12" rx="2" fill="#6B7280"/>
      <rect x="3" y="3" width="18" height="6" rx="2" fill="#3B82F6"/>
      <path d="M10 12h4" stroke="#FFFFFF" stroke-width="2" stroke-linecap="round"/>
    </svg>`,
    color: '#6B7280',
    emoji: '📦'
  },

  history: {
    svg: `<svg width="24" height="24" viewBox="0 0 24 24" fill="none">
      <circle cx="12" cy="12" r="10" stroke="#6B7280" stroke-width="2"/>
      <path d="M12 6v6l4 2" stroke="#6B7280" stroke-width="2" stroke-linecap="round"/>
      <path d="M4 12h2M2 12h1" stroke="#3B82F6" stroke-width="2" stroke-linecap="round"/>
    </svg>`,
    color: '#6B7280',
    emoji: '🕐'
  },

  loading: {
    svg: `<svg width="24" height="24" viewBox="0 0 24 24" fill="none">
      <circle cx="12" cy="12" r="10" stroke="#E5E7EB" stroke-width="2"/>
      <path d="M22 12c0-5.52-4.48-10-10-10" stroke="#3B82F6" stroke-width="2" stroke-linecap="round">
        <animateTransform attributeName="transform" type="rotate" dur="1s" values="0 12 12;360 12 12" repeatCount="indefinite"/>
      </path>
    </svg>`,
    color: '#3B82F6',
    emoji: '⏳'
  },

  expand: {
    svg: `<svg width="24" height="24" viewBox="0 0 24 24" fill="none">
      <path d="M8 3H5a2 2 0 00-2 2v3M21 8V5a2 2 0 00-2-2h-3M16 21h3a2 2 0 002-2v-3M3 16v3a2 2 0 002 2h3" stroke="#6B7280" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
    </svg>`,
    color: '#6B7280',
    emoji: '🔍'
  },

  collapse: {
    svg: `<svg width="24" height="24" viewBox="0 0 24 24" fill="none">
      <path d="M16 8l-4-4-4 4M8 16l4 4 4-4" stroke="#6B7280" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
    </svg>`,
    color: '#6B7280',
    emoji: '🗜️'
  },

  // Additional Navigation Icons
  arrow: {
    svg: `<svg width="24" height="24" viewBox="0 0 24 24" fill="none">
      <path d="M5 12h14m0 0l-7-7m7 7l-7 7" stroke="#3B82F6" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
    </svg>`,
    color: '#3B82F6',
    emoji: '➡️'
  },

  'arrow-left': {
    svg: `<svg width="24" height="24" viewBox="0 0 24 24" fill="none">
      <path d="M19 12H5m0 0l7 7m-7-7l7-7" stroke="#3B82F6" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
    </svg>`,
    color: '#3B82F6',
    emoji: '⬅️'
  },

  'arrow-right': {
    svg: `<svg width="24" height="24" viewBox="0 0 24 24" fill="none">
      <path d="M5 12h14m0 0l-7-7m7 7l-7 7" stroke="#3B82F6" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
    </svg>`,
    color: '#3B82F6',
    emoji: '➡️'
  },

  // Cancel variations
  'cancel-circle': {
    svg: `<svg width="24" height="24" viewBox="0 0 24 24" fill="none">
      <circle cx="12" cy="12" r="10" fill="#EF4444"/>
      <line x1="15" y1="9" x2="9" y2="15" stroke="#FFFFFF" stroke-width="3" stroke-linecap="round"/>
      <line x1="9" y1="9" x2="15" y2="15" stroke="#FFFFFF" stroke-width="3" stroke-linecap="round"/>
    </svg>`,
    color: '#EF4444',
    emoji: '❌'
  },

  // Calendar variations
  'calendar-cancel': {
    svg: `<svg width="24" height="24" viewBox="0 0 24 24" fill="none">
      <rect x="5" y="6" width="14" height="13" rx="3" fill="#EF4444"/>
      <rect x="5" y="6" width="14" height="4" rx="3" fill="#B91C1C"/>
      <line x1="9" y1="4" x2="9" y2="8" stroke="#6B7280" stroke-width="1.5" stroke-linecap="round"/>
      <line x1="15" y1="4" x2="15" y2="8" stroke="#6B7280" stroke-width="1.5" stroke-linecap="round"/>
      <!-- X mark -->
      <line x1="10" y1="12" x2="14" y2="16" stroke="#FFFFFF" stroke-width="2" stroke-linecap="round"/>
      <line x1="14" y1="12" x2="10" y2="16" stroke="#FFFFFF" stroke-width="2" stroke-linecap="round"/>
    </svg>`,
    color: '#EF4444',
    emoji: '📅❌'
  },
  store: {
    svg: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
      <path d="M4 7h16l-1 10H5L4 7z"/>
      <path d="M8 7V5a1 1 0 011-1h6a1 1 0 011 1v2"/>
    </svg>`,
    color: '#F59E0B',
    emoji: '🏪'
  },
  business: {
    svg: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
      <rect x="3" y="3" width="18" height="18" rx="2"/>
      <path d="M9 9h.01M15 9h.01M9 15h.01M15 15h.01"/>
    </svg>`,
    color: '#3B82F6',
    emoji: '🏢'
  },
  save: {
    svg: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
      <path d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2z"/>
      <polyline points="17,21 17,13 7,13 7,21"/>
      <polyline points="7,3 7,8 15,8"/>
    </svg>`,
    color: '#10B981',
    emoji: '💾'
  },
  layers: {
    svg: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
      <polygon points="12,2 2,7 12,12 22,7 12,2"/>
      <polyline points="2,17 12,22 22,17"/>
      <polyline points="2,12 12,17 22,12"/>
    </svg>`,
    color: '#8B5CF6',
    emoji: '📄'
  },
  'user-check': {
    svg: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
      <path d="M16 21v-2a4 4 0 00-4-4H6a4 4 0 00-4 4v2"/>
      <circle cx="9" cy="7" r="4"/>
      <polyline points="16,11 18,13 22,9"/>
    </svg>`,
    color: '#10B981',
    emoji: '✅'
  },
  clear: {
    svg: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
      <line x1="18" y1="6" x2="6" y2="18"/>
      <line x1="6" y1="6" x2="18" y2="18"/>
    </svg>`,
    color: '#EF4444',
    emoji: '🗑️'
  },

  // Theme Icons
  sun: {
    svg: `<svg width="24" height="24" viewBox="0 0 24 24" fill="none">
      <circle cx="12" cy="12" r="5" stroke="#F59E0B" stroke-width="2" fill="#FEF3C7"/>
      <line x1="12" y1="1" x2="12" y2="3" stroke="#F59E0B" stroke-width="2" stroke-linecap="round"/>
      <line x1="12" y1="21" x2="12" y2="23" stroke="#F59E0B" stroke-width="2" stroke-linecap="round"/>
      <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" stroke="#F59E0B" stroke-width="2" stroke-linecap="round"/>
      <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" stroke="#F59E0B" stroke-width="2" stroke-linecap="round"/>
      <line x1="1" y1="12" x2="3" y2="12" stroke="#F59E0B" stroke-width="2" stroke-linecap="round"/>
      <line x1="21" y1="12" x2="23" y2="12" stroke="#F59E0B" stroke-width="2" stroke-linecap="round"/>
      <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" stroke="#F59E0B" stroke-width="2" stroke-linecap="round"/>
      <line x1="18.36" y1="5.64" x2="19.78" y2="4.22" stroke="#F59E0B" stroke-width="2" stroke-linecap="round"/>
    </svg>`,
    color: ICON_COLORS.warning,
    emoji: '☀️'
  },

  moon: {
    svg: `<svg width="24" height="24" viewBox="0 0 24 24" fill="none">
      <path d="M21 12.79A9 9 0 1111.21 3 7 7 0 0021 12.79z" fill="#6366F1" stroke="#4F46E5" stroke-width="2"/>
    </svg>`,
    color: ICON_COLORS.primary,
    emoji: '🌙'
  },
  clipboard: {
    svg: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
      <path d="M16 4h2a2 2 0 012 2v14a2 2 0 01-2 2H6a2 2 0 01-2-2V6a2 2 0 012-2h2"/>
      <rect x="8" y="2" width="8" height="4" rx="1" ry="1"/>
    </svg>`,
    color: '#6B7280',
    emoji: '📋'
  },
  print: {
    svg: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
      <polyline points="6,9 6,2 18,2 18,9"/>
      <path d="M6,18H4a2,2 0 0,1-2-2v-5a2,2 0 0,1,2-2H20a2,2 0 0,1,2,2v5a2,2 0 0,1-2,2H18"/>
      <polyline points="6,14 18,14 18,22 6,22 6,14"/>
    </svg>`,
    color: '#6B7280',
    emoji: '🖨️'
  },
  tag: {
    svg: `<svg width="24" height="24" viewBox="0 0 24 24" fill="none">
      <!-- Tag icon with vibrant colors -->
      <path d="M20.59 13.41l-7.17 7.17a2 2 0 01-2.83 0L2 12V2h10l8.59 8.59a2 2 0 010 2.82z" fill="#8B5CF6"/>
      <path d="M7 7h0" stroke="#FFFFFF" stroke-width="3" stroke-linecap="round"/>
      <circle cx="7" cy="7" r="1.5" fill="#FFFFFF"/>
    </svg>`,
    color: ICON_COLORS.purple,
    emoji: '🏷️'
  },
  target: {
    svg: `<svg width="24" height="24" viewBox="0 0 24 24" fill="none">
      <!-- Target/bullseye icon -->
      <circle cx="12" cy="12" r="10" fill="none" stroke="#6B7280" stroke-width="2"/>
      <circle cx="12" cy="12" r="6" fill="none" stroke="#6B7280" stroke-width="2"/>
      <circle cx="12" cy="12" r="2" fill="#6B7280"/>
    </svg>`,
    color: ICON_COLORS.secondary,
    emoji: '🎯'
  },
  values: {
    svg: `<svg width="24" height="24" viewBox="0 0 24 24" fill="none">
      <!-- Values/star icon -->
      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" fill="#F59E0B"/>
    </svg>`,
    color: ICON_COLORS.warning,
    emoji: '⭐'
  },

  // Landing Pages & Settings Icons
  globe: {
    svg: `<svg width="24" height="24" viewBox="0 0 24 24" fill="none">
      <!-- Globe/world icon -->
      <circle cx="12" cy="12" r="10" fill="#3B82F6"/>
      <path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" stroke="#FFFFFF" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
      <circle cx="12" cy="12" r="10" stroke="#1E40AF" stroke-width="2"/>
    </svg>`,
    color: ICON_COLORS.primary,
    emoji: '🌍'
  },

  page: {
    svg: `<svg width="24" height="24" viewBox="0 0 24 24" fill="none">
      <!-- Document/page icon -->
      <rect x="6" y="3" width="12" height="16" rx="2" fill="#3B82F6"/>
      <!-- Page header -->
      <rect x="6" y="3" width="12" height="4" rx="2" fill="#1E40AF"/>
      <!-- Content lines -->
      <line x1="9" y1="10" x2="15" y2="10" stroke="#FFFFFF" stroke-width="1.5" stroke-linecap="round"/>
      <line x1="9" y1="12" x2="13" y2="12" stroke="#FFFFFF" stroke-width="1.5" stroke-linecap="round"/>
      <line x1="9" y1="14" x2="14" y2="14" stroke="#FFFFFF" stroke-width="1.5" stroke-linecap="round"/>
      <!-- Landing page indicator -->
      <circle cx="16" cy="16" r="3" fill="#10B981"/>
      <path d="M15 16l1 1 2-2" stroke="#FFFFFF" stroke-width="1" stroke-linecap="round" stroke-linejoin="round"/>
    </svg>`,
    color: ICON_COLORS.primary,
    emoji: '📄'
  },

  settings: {
    svg: `<svg width="24" height="24" viewBox="0 0 24 24" fill="none">
      <!-- Settings gear icon -->
      <circle cx="12" cy="12" r="8" fill="#6B7280"/>
      <circle cx="12" cy="12" r="4" fill="#3B82F6"/>
      <!-- Gear teeth -->
      <rect x="11" y="2" width="2" height="4" rx="1" fill="#8B5CF6"/>
      <rect x="11" y="18" width="2" height="4" rx="1" fill="#8B5CF6"/>
      <rect x="2" y="11" width="4" height="2" rx="1" fill="#8B5CF6"/>
      <rect x="18" y="11" width="4" height="2" rx="1" fill="#8B5CF6"/>
      <!-- Diagonal teeth -->
      <rect x="18.8" y="4.8" width="2" height="2" rx="1" fill="#F59E0B" transform="rotate(45 19.8 5.8)"/>
      <rect x="18.8" y="16.8" width="2" height="2" rx="1" fill="#F59E0B" transform="rotate(45 19.8 17.8)"/>
      <rect x="3.2" y="4.8" width="2" height="2" rx="1" fill="#F59E0B" transform="rotate(45 4.2 5.8)"/>
      <rect x="3.2" y="16.8" width="2" height="2" rx="1" fill="#F59E0B" transform="rotate(45 4.2 17.8)"/>
      <!-- Center indicator -->
      <circle cx="12" cy="12" r="2" fill="#FFFFFF"/>
    </svg>`,
    color: ICON_COLORS.secondary,
    emoji: '⚙️'
  },

  preview: {
    svg: `<svg width="24" height="24" viewBox="0 0 24 24" fill="none">
      <!-- Eye icon for preview -->
      <path d="M12 4C6 4 1.5 8.5 1 12c.5 3.5 5 8 11 8s10.5-4.5 11-8c-.5-3.5-5-8-11-8z" fill="#3B82F6"/>
      <!-- Eye pupil -->
      <circle cx="12" cy="12" r="4" fill="#1E40AF"/>
      <circle cx="12" cy="12" r="2" fill="#FFFFFF"/>
      <!-- Preview indicator -->
      <circle cx="18" cy="6" r="2" fill="#10B981"/>
      <path d="M17 6h2" stroke="#FFFFFF" stroke-width="0.8" stroke-linecap="round"/>
      <path d="M18 5v2" stroke="#FFFFFF" stroke-width="0.8" stroke-linecap="round"/>
    </svg>`,
    color: ICON_COLORS.primary,
    emoji: '👁️'
  }
};

// Icon aliases for backward compatibility
ICONS['refresh-cw'] = ICONS.refresh;  // refresh-cw is an alias for refresh
ICONS.x = ICONS.close;                // x is an alias for close

/**
 * Icon Utility Functions
 */
export class IconUtils {
  /**
   * Get icon SVG with optional color override
   * @param {string} iconName - Name of the icon from ICONS constant
   * @param {string} color - Optional color override (defaults to icon's default color)
   * @param {number} size - Optional size override (defaults to 24)
   * @returns {string} SVG string ready for innerHTML
   */
  static getSvg(iconName, color = null, size = 24) {
    const icon = ICONS[iconName];
    if (!icon) {
      console.warn(`Icon '${iconName}' not found`);
      return '';
    }

    let svgElement = icon.svg
      .replace(/width="24"/g, `width="${size}"`)
      .replace(/height="24"/g, `height="${size}"`);

    // Only replace currentColor for icons that use it (not the index.html icons with hard-coded colors)
    if (color && svgElement.includes('currentColor')) {
      svgElement = svgElement.replace(/currentColor/g, color);
    }

    return svgElement;
  }

  /**
   * Get emoji fallback for icon
   * @param {string} iconName - Name of the icon
   * @returns {string} Emoji character
   */
  static getEmoji(iconName) {
    const icon = ICONS[iconName];
    return icon ? icon.emoji : '📄';
  }

  /**
   * Create icon HTML element
   * @param {string} iconName - Name of the icon
   * @param {Object} options - Options object
   * @param {string} options.color - Color override
   * @param {number} options.size - Size override
   * @param {string} options.className - Additional CSS classes
   * @param {string} options.title - Title attribute for accessibility
   * @returns {string} Complete HTML string
   */
  static createIcon(iconName, options = {}) {
    const {
      color = null,
      size = 24,
      className = '',
      title = ''
    } = options;

    const svg = this.getSvg(iconName, color, size);
    const titleAttr = title ? ` title="${title}"` : '';
    const classAttr = className ? ` class="${className}"` : '';

    return `<span${classAttr}${titleAttr}>${svg}</span>`;
  }
}

// Export individual icon getters for convenience
export const getIcon = IconUtils.getSvg;
export const createIcon = IconUtils.createIcon;
export const getIconEmoji = IconUtils.getEmoji;
